/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { SerializedStyles } from "@emotion/react";
import { IAppState } from "../../../core/redux/IAppState";
import { ColumnConfig } from "../../../layout/table/columnConfig";
import {
  LiftAccessRuleSetRow,
  MaxDaysWidth,
  BlackoutDOWWidth,
  IDWidth,
  SeasonWidth,
  FulfillmentSeasonWidth,
  BlackoutDateRangeWidth,
  DescriptionWidth,
  LocationWidth,
  EffectiveDateWidth,
  ExpirationDateWidth,
  EffectiveTimeWidth,
  MenuOptionsWidth,
  SelectWidth,
} from "./LiftAccessRuleSetRow";
import Table from "../../../layout/table/Table";
import { getResource } from "../../../language/resources/getResource";
import { css } from "@emotion/react";
import { LiftAccessRuleSet, liftAccessRuleSetKey } from "./models";

const HeaderFontSize = 14;

const IDHeaderCss = css`  
  font-size: ${HeaderFontSize}px; !important;
  width: ${IDWidth}px !important;
  max-width: ${IDWidth}px !important;
`;

const DescriptionCss = css`
  width: ${DescriptionWidth}px !important;
  font-size: ${HeaderFontSize}px; !important;
`;

const SeasonHeaderCss = css`
  font-size: ${HeaderFontSize}px; !important;
  width: ${SeasonWidth}px !important;
  max-width: ${SeasonWidth}px !important;
`;

const FulfillmentSeasonHeaderCss = css`
  font-size: ${HeaderFontSize}px; !important;
  width: ${FulfillmentSeasonWidth}px !important;
  max-width: ${FulfillmentSeasonWidth}px !important;
`;

const LocationHeaderCss = css`
  width: ${LocationWidth}px !important;
  font-size: ${HeaderFontSize}px; !important;
`;

const EffectiveDateHeaderCss = css`
  font-size: ${HeaderFontSize}px; !important;
  width: ${EffectiveDateWidth}px !important;
  max-width: ${EffectiveDateWidth}px !important;
`;

const ExpirationDateHeaderCss = css`
  font-size: ${HeaderFontSize}px; !important;
  width: ${ExpirationDateWidth}px !important;
  max-width: ${ExpirationDateWidth}px !important;
`;

const EffectiveTimeHeaderCss = css`
  font-size: ${HeaderFontSize}px; !important;
  width: ${EffectiveTimeWidth}px !important;
  max-width: ${EffectiveTimeWidth}px !important;
`;

const MaxDaysHeaderCss = css`
  font-size: ${HeaderFontSize}px; !important;
  width: ${MaxDaysWidth}px !important;
  max-width: ${MaxDaysWidth}px !important;
`;

const BlckoutDateRangeHeaderCss = css`
  font-size: ${HeaderFontSize}px; !important;
  width: ${BlackoutDateRangeWidth}px !important;
  max-width: ${BlackoutDateRangeWidth}px !important;
`;

const BlackoutDOWHeaderCss = css`
  font-size: ${HeaderFontSize}px; !important;
  width: ${BlackoutDOWWidth}px !important;
  max-width: ${BlackoutDOWWidth}px !important;
`;

const MenuOptionsHeaderCss = css`
  width: ${MenuOptionsWidth}px !important;
  max-width: ${MenuOptionsWidth}px !important;
`;

const SelectHeaderCss = css`
  width: ${SelectWidth}px !important;
  max-width: ${SelectWidth}px !important;
`;

type Props = {
  rules: LiftAccessRuleSet[];
  isLoading?: boolean;
  noDataMessage: string;
  onSortRules: (
    property: keyof LiftAccessRuleSet,
    asc: boolean,
    useConcatenatedStrong: boolean,
    ruleProperty?: string,
  ) => LiftAccessRuleSet[];
  onEditRuleSet: (rulesSet: LiftAccessRuleSet) => void;
  onDeleteRuleSet: (rulesSet: LiftAccessRuleSet) => void;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onToggleSelect?: (
    ruleSet: LiftAccessRuleSet,
    selected: boolean,
    shiftKey: boolean,
  ) => void;
  /** Rows stay readable and sortable, but selection and row actions are off. */
  locked?: boolean;
  rootCss?: SerializedStyles;
};

export const LiftAccessRuleSetTable = (props: Props) => {
  const resources = useSelector((state: IAppState) => state.locale);

  const onSortRules = props.onSortRules;
  const selectable = props.selectable;

  // Table re-sorts its whole data set whenever the columns change by value, and
  // these hold functions — so this must stay stable, and so must onSortRules.
  const columnConfig = useMemo<Array<ColumnConfig<LiftAccessRuleSet>>>(() => {
    const makeSortBy =
      (
        property: keyof LiftAccessRuleSet,
        ruleProperty?: string,
        useConcatenatedString: boolean = false,
      ) =>
      (asc: boolean, data: LiftAccessRuleSet[]) =>
        onSortRules(property, asc, useConcatenatedString, ruleProperty);

    const selectColumn: ColumnConfig<LiftAccessRuleSet> = {
      id: 0,
      title: "",
      headerCss: SelectHeaderCss,
    };

    return [
      ...(selectable ? [selectColumn] : []),
      {
        id: 1,
        tooltipText: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_ENTITLEMENT_TYPE_ID",
        ),
        title: getResource(resources, "LIFT_ACCESS_RULES_TABLE_ID"),
        onSort: makeSortBy("entitlementTypeId"),
        headerCss: IDHeaderCss,
        defaultSortingColumn: true,
        defaultSortingDirection: "asc",
      },
      {
        id: 2,
        tooltipText: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_ENTITLEMENT_DESCRIPTION",
        ),
        title: getResource(resources, "LIFT_ACCESS_RULES_TABLE_DESCRIPTION"),
        onSort: makeSortBy("entitlementDescription"),
        headerCss: DescriptionCss,
      },
      {
        id: 3,
        tooltipText: getResource(resources, "LIFT_ACCESS_RULES_TABLE_SEASON"),
        title: getResource(resources, "LIFT_ACCESS_RULES_TABLE_SEASON"),
        onSort: makeSortBy("sellingSeasonDescription"),
        headerCss: SeasonHeaderCss,
      },
      {
        id: 4,
        tooltipText: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_FULFILLMENT_SEASON",
        ),
        title: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_FULFILLMENT_SEASON",
        ),
        onSort: makeSortBy("fulfillmentSeasonDescription"),
        headerCss: FulfillmentSeasonHeaderCss,
      },
      {
        id: 5,
        tooltipText: getResource(resources, "LIFT_ACCESS_RULES_TABLE_LOCATION"),
        title: getResource(resources, "LIFT_ACCESS_RULES_TABLE_LOCATION"),
        onSort: makeSortBy("locationName"),
        headerCss: LocationHeaderCss,
      },
      {
        id: 6,
        tooltipText: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_SEASON_EFFECTIVE_DATE",
        ),
        title: getResource(resources, "LIFT_ACCESS_RULES_TABLE_EFFECTIVE_DATE"),
        onSort: makeSortBy("effectiveDateRangeRules", "effectiveDateStart"),
        headerCss: EffectiveDateHeaderCss,
      },
      {
        id: 7,
        tooltipText: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_SEASON_EXPIRATION_DATE",
        ),
        title: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_EXPIRATION_DATE",
        ),
        onSort: makeSortBy("effectiveDateRangeRules", "effectiveDateEnd"),
        headerCss: ExpirationDateHeaderCss,
      },
      {
        id: 8,
        tooltipText: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_SEASON_EFFECTIVE_TIME",
        ),
        title: getResource(resources, "LIFT_ACCESS_RULES_TABLE_EFFECTIVE_TIME"),
        onSort: makeSortBy("effectiveDateRangeRules", "effectiveTimeSlotStart"),
        headerCss: EffectiveTimeHeaderCss,
      },
      {
        id: 9,
        tooltipText: getResource(resources, "LIFT_ACCESS_RULES_TABLE_MAX_DAYS"),
        title: getResource(resources, "LIFT_ACCESS_RULES_TABLE_MAX_DAYS"),
        headerCss: MaxDaysHeaderCss,
        onSort: makeSortBy("maxDaysRules", "numberOfDays"),
      },
      {
        id: 10,
        tooltipText: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_BLACKOUT_DATE_RANGES",
        ),
        title: getResource(resources, "LIFT_ACCESS_RULES_TABLE_BLACKOUT"),
        onSort: makeSortBy("blackoutDateRangeRules", "blackoutDateStart"),
        headerCss: BlckoutDateRangeHeaderCss,
      },
      {
        id: 11,
        tooltipText: getResource(
          resources,
          "LIFT_ACCESS_RULES_TABLE_BLACKOUT_DAYS_OF_WEEK",
        ),
        title: getResource(resources, "LIFT_ACCESS_RULES_TABLE_BLACKOUT_DOW"),
        onSort: makeSortBy("blackoutDayOfWeekRules", "dayOfWeekId", true),
        headerCss: BlackoutDOWHeaderCss,
      },
      {
        id: 12,
        title: "",
        tooltipText: getResource(resources, "ACCESS_RULES_OPTIONS"),
        headerCss: MenuOptionsHeaderCss,
      },
    ];
  }, [resources, selectable, onSortRules]);

  const handleRowDoubleClick = (ruleSet: LiftAccessRuleSet) => {
    props.onEditRuleSet(ruleSet);
  };

  const handleEdit = (ruleSet: LiftAccessRuleSet) => {
    props.onEditRuleSet(ruleSet);
  };

  const handleDelete = (ruleSet: LiftAccessRuleSet) => {
    props.onDeleteRuleSet(ruleSet);
  };

  const renderRow = (item: LiftAccessRuleSet, index: number) => (
    <LiftAccessRuleSetRow
      key={index}
      ruleSet={item}
      selectable={props.selectable}
      selected={props.selectedKeys?.has(liftAccessRuleSetKey(item))}
      onToggleSelect={props.onToggleSelect}
      locked={props.locked}
      onDoubleClick={handleRowDoubleClick}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );

  return (
    <Table
      key="liftAccessRuleSetTable"
      noDataMessage={props.noDataMessage}
      isLoading={!!props.isLoading}
      columns={columnConfig}
      data={props.rules}
      renderRow={renderRow}
      rootCss={props.rootCss}
    />
  );
};
